import React from 'react';
import { useTranslation } from "react-i18next";
import { Col, Row, Container, Button } from 'react-bootstrap';
import { FaRobot, FaSearch, FaFileAlt } from 'react-icons/fa';
import { Paths } from 'paths';
import "styles/theme.css";
import Page from 'components/page';

const FeatureCard = ({ title, description, icon: Icon }) => (
    <Col md={4} className="mb-4">
        <div className="card h-100 shadow-sm transition-all"
            style={{
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 .5rem 1rem rgba(0,0,0,.15)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)';
            }}
        >
            <div className="card-body text-center">
                <div className="feature-icon mb-3">
                    <Icon className="fs-2 custom-feature-icon" />
                </div>
                <h5 className="card-title">{title}</h5>
                <p className="card-text text-muted">{description}</p>
            </div>
        </div>
    </Col>
);

const features = [
    {
        title: "Advanced LLM Support",
        description: "Run powerful models like DeepSeek-R1 for advanced reasoning and Qwen2.5-VL for image & video understanding, with enterprise-grade deployment",
        icon: FaRobot
    },
    {
        title: "Model Management & RAG",
        description: "Efficiently manage and serve multiple modern LLM models, from reasoning-focused to multimodal, with built-in versioning and API endpoints. Includes secure RAG support for sensitive data within your security perimeter",
        icon: FaSearch
    },
    {
        title: "Performance Monitoring",
        description: "Comprehensive monitoring and logging of model inference metrics, latency, and resource utilization across your entire LLM fleet",
        icon: FaFileAlt
    }
];

const Home = () => {
    const [t] = useTranslation('common');

    return (
        <Page title={t('LocalIQ - Enterprise LLM Inference Server')} description={t('Deploy and manage powerful LLM inference servers with built-in load balancing, fault tolerance, and comprehensive monitoring for enterprise AI applications.')} canonicalUrl={Paths.Home.path}>
            <Container className="min-vh-100 my-5">
                <section className="section-header pt-3 pb-lg-3 overflow-hidden" id="home">
                    <Container>
                    <Row>
                        <Col xs={12} className="text-center">
                        <h1 className="fw-normal">
                            LocalIQ LLM Inference Server
                        </h1>
                        <p className="text-muted fw-light mb-5 h5">Deploy your LLM inference server with built-in load balancing and fault tolerance for high availability, on-premise or in the cloud</p>
                        </Col>
                    </Row>
                    </Container>
                </section>
                <section className="py-1">
                    <Container>
                        <Row>
                            {features.map((feature, index) => (
                                <FeatureCard key={index} {...feature} />
                            ))}
                        </Row>
                        </Container>
                </section>

                <section className="py-5">
                    <Container>
                        <Row className="justify-content-center">
                            <Col md={10}>
                                <h2 className="text-center mb-4">How It Works</h2>
                                <div className="d-flex flex-column gap-4">
                                    <div className="p-4 bg-light rounded">
                                        <h4>Two-Component Architecture</h4>
                                        <p>LocalIQ consists of two main components working in harmony:</p>
                                        <ul>
                                            <li><strong>Server</strong> - Functions as the web panel, API endpoint, and coordinator for workers</li>
                                            <li><strong>Workers</strong> - Dedicated to serving LLM models via vLLM on GPU hardware nodes</li>
                                        </ul>
                                    </div>
                                    <div className="p-4 bg-light rounded">
                                        <h4>Intelligent Workload Management</h4>
                                        <p>The coordinator handles request load-balancing and ensures fault-tolerance across your deployment. Workers process LLM inference requests on modern NVIDIA GPUs, delivering optimal performance for your AI applications.</p>
                                    </div>
                                    <div className="p-4 bg-light rounded">
                                        <h4>Simple Deployment</h4>
                                        <p>Deploy the server and a set of workers on nodes with GPUs. The system supports modern NVIDIA GPUs and scales according to your needs.</p>
                                    </div>
                                    <div className="p-4 bg-light rounded">
                                        <h4>Comprehensive Management</h4>
                                        <p>The intuitive web panel provides:</p>
                                        <ul>
                                            <li>Worker management and monitoring</li>
                                            <li>Interactive chat interface with LLM models</li>
                                            <li>API endpoint token management</li>
                                            <li>Performance monitoring and analytics</li>
                                        </ul>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
                <section className="py-5 text-center">
                    <Container>
                        <Row className="justify-content-center">
                            <Col md={8}>
                                <h2 className="mb-4">Ready to Experience LocalIQ?</h2>
                                <p className="text-muted mb-4">Deploy your own on-premise LLM inference server in minutes and unlock the full potential of large language models with complete control over your data and infrastructure.</p>
                                <div className="d-flex justify-content-center gap-3">
                                    <Button className="custom-button" variant="light" size="lg" href={Paths.Deploy.path}>Deploy On-Premise</Button>
                                    <Button className="custom-button" variant="light" size="lg" href={Paths.Blog.path}>Learn More</Button>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>

            </Container>
        </Page>
    );
}

export default Home;